import { useMutation, useQuery } from '@tanstack/react-query';
import { METABASE_API_URL, metabaseApi } from '../../core/api';
import {
  type DashboardQueryParameter,
  type DatasetQuery,
  type FieldMetadata,
  fieldValuesSchema,
  type FullCard,
  fullCardSchema,
  parseQueryResult,
  type QueryMetadata,
  queryMetadataSchema,
  type QueryParameter,
  type QueryResult,
  queryResultSchema,
} from '../../core/schemas/metabase';

export const QUERY_KEY_CARD = 'metabase-card';
export const QUERY_KEY_CARD_QUERY = 'metabase-card-query';

export const useGetFullCard = (cardId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEY_CARD, cardId],
    queryFn: async () => {
      const { data } = await metabaseApi.get(
        `${METABASE_API_URL}/card/${cardId}`,
        { params: { 'legacy-mbql': true } }
      );

      const parsed = fullCardSchema.safeParse(data);
      if (!parsed.success) {
        // oxlint-disable-next-line no-console
        console.error('Failed to parse full card:', parsed.error);
        throw new Error('Failed to parse card metadata');
      }

      return parsed.data;
    },
    enabled: !!cardId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const useCardQueryData = (
  cardId: number | undefined,
  options?: {
    parameters?: QueryParameter[];
    enabled?: boolean;
    ignoreCache?: boolean;
  }
) =>
  useQuery({
    queryKey: [QUERY_KEY_CARD_QUERY, cardId, options?.parameters],
    queryFn: async () => {
      const { data } = await metabaseApi.post(
        `${METABASE_API_URL}/card/${cardId}/query`,
        {
          ignore_cache: options?.ignoreCache ?? false,
          collection_preview: false,
          parameters: options?.parameters ?? [],
        }
      );

      return parseQueryResult(data);
    },
    enabled: !!cardId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

export const QUERY_KEY_DASHBOARD_CARD_QUERY = 'dashboard-card-query';

export const useDashboardCardQuery = (
  dashboardId: number | undefined,
  dashcardId: number | undefined,
  cardId: number | undefined,
  options?: {
    parameters?: DashboardQueryParameter[];
    enabled?: boolean;
  }
) =>
  useQuery({
    queryKey: [
      QUERY_KEY_DASHBOARD_CARD_QUERY,
      dashboardId,
      dashcardId,
      cardId,
      options?.parameters,
    ],
    queryFn: async () => {
      const { data } = await metabaseApi.post(
        `${METABASE_API_URL}/dashboard/${dashboardId}/dashcard/${dashcardId}/card/${cardId}/query`,
        {
          parameters: options?.parameters ?? [],
          dashboard_id: dashboardId,
        }
      );

      const parsed = queryResultSchema.safeParse(data);
      if (!parsed.success) {
        // oxlint-disable-next-line no-console
        console.error(
          'Failed to parse dashboard card query result:',
          parsed.error
        );
        throw new Error('Failed to parse query result');
      }

      return parsed.data;
    },
    enabled:
      !!dashboardId && !!dashcardId && !!cardId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

export const useExecuteCardQuery = () =>
  useMutation({
    mutationFn: async ({
      cardId,
      parameters = [],
      ignoreCache = false,
    }: {
      cardId: number;
      parameters?: QueryParameter[];
      ignoreCache?: boolean;
    }) => {
      const { data } = await metabaseApi.post(
        `${METABASE_API_URL}/card/${cardId}/query`,
        {
          ignore_cache: ignoreCache,
          collection_preview: false,
          parameters,
        }
      );

      return parseQueryResult(data);
    },
  });

export const useUpdateCardData = () =>
  useMutation({
    mutationFn: async ({
      cardId,
      payload,
    }: {
      cardId: number;
      payload: Partial<FullCard>;
    }) => {
      const { data } = await metabaseApi.put(
        `${METABASE_API_URL}/card/${cardId}`,
        payload
      );
      return fullCardSchema.parse(data);
    },
  });

export const useExecuteDatasetQuery = () =>
  useMutation({
    mutationFn: async ({
      datasetQuery,
    }: {
      datasetQuery: DatasetQuery;
    }): Promise<QueryResult> => {
      const { data } = await metabaseApi.post(`${METABASE_API_URL}/dataset`, {
        ...datasetQuery,
        parameters: [],
      });

      const parsed = queryResultSchema.safeParse(data);
      if (!parsed.success) {
        // oxlint-disable-next-line no-console
        console.error('Failed to parse dataset query result:', parsed.error);
        throw new Error('Failed to parse dataset query result');
      }

      return parsed.data;
    },
  });

const QUERY_KEY_DATASET = 'metabase-dataset';

export const useDatasetQuery = (
  datasetQuery: DatasetQuery | undefined,
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [QUERY_KEY_DATASET, datasetQuery],
    queryFn: async () => {
      const { data } = await metabaseApi.post(`${METABASE_API_URL}/dataset`, {
        ...datasetQuery,
        parameters: [],
      });

      const parsed = queryResultSchema.safeParse(data);
      if (!parsed.success) {
        // oxlint-disable-next-line no-console
        console.error('Failed to parse dataset query result:', parsed.error);
        throw new Error('Failed to parse dataset query result');
      }

      return parsed.data;
    },
    enabled: !!datasetQuery && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

const QUERY_KEY_CARD_QUERY_METADATA = 'metabase-card-query-metadata';
export const useGetCardQueryMetadata = (cardId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEY_CARD_QUERY_METADATA, cardId],
    queryFn: async () => {
      const { data } = await metabaseApi.get(
        `${METABASE_API_URL}/card/${cardId}/query_metadata`
      );

      const parsed = queryMetadataSchema.safeParse(data);
      if (!parsed.success) {
        // oxlint-disable-next-line no-console
        console.error('Failed to parse card query metadata:', parsed.error);
        throw new Error('Failed to parse card query metadata');
      }

      return parsed.data;
    },
    enabled: !!cardId,
    staleTime: 1000 * 60 * 10,
  });

export const extractFieldsFromQueryMetadata = (
  metadata: QueryMetadata | undefined
): FieldMetadata[] => {
  if (!metadata?.tables) return [];

  return metadata.tables.flatMap(table =>
    (table.fields ?? []).map(field => ({
      name: field.name,
      display_name: field.display_name,
      base_type: field.base_type,
      effective_type: field.effective_type,
      semantic_type: field.semantic_type,
      database_type: field.database_type,
      has_field_values: field.has_field_values,
      id: field.id,
      table_id: field.table_id,
      field_ref: field.field_ref ?? ['field', field.id, null],
      fingerprint: field.fingerprint,
    }))
  );
};

const QUERY_KEY_FIELD_VALUES = 'metabase-field-values';

export const useGetFieldValues = (fieldId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEY_FIELD_VALUES, fieldId],
    queryFn: async () => {
      const { data } = await metabaseApi.get(
        `${METABASE_API_URL}/field/${fieldId}/values`
      );

      const parsed = fieldValuesSchema.safeParse(data);
      if (!parsed.success) {
        // oxlint-disable-next-line no-console
        console.error('Failed to parse field values:', parsed.error);
        throw new Error('Failed to parse field values');
      }

      return parsed.data;
    },
    enabled: !!fieldId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
